import {
  EVENT_CATEGORIES,
  getEventCategory,
  getCategoryStyle,
} from '../../utils/categories';
import { COMMON_TIMEZONES } from '../../utils/timezone';
import { LIMITS } from '../../utils/validation';
import { getCategoryIcon } from '../UI/Icons';
import FormField, { ValidatedInput, ValidatedTextarea } from '../UI/FormField';
import Select from '../UI/Select';
import DatePicker from '../UI/DatePicker';
import TimePicker from '../UI/TimePicker';
import TravelDetailsSection from './TravelDetailsSection';
import AttachmentSection from './AttachmentSection';

// Convert timezone array to Select options format
const TIMEZONE_OPTIONS = COMMON_TIMEZONES.map(tz => ({
  value: tz.value,
  label: tz.label,
}));

/**
 * EventForm - Form fields for creating/editing an event
 * Includes: title, category, dates/times, timezones, location, notes
 * Plus collapsible sections for travel details and attachments
 */
export default function EventForm({
  eventData,
  onChange,
  errors = {},
  needsSeparateTimezones,
  // Travel details
  showTravelDetails,
  onToggleTravelDetails,
  // Attachments
  isEditing,
  showAttachments,
  onToggleAttachments,
  attachments,
  onAttachmentsChange,
  eventId,
  loadingAttachments,
  pendingAttachments,
  onPendingChange,
  tempId,
  uploadError,
  // Trips
  trips = [],
  // Common
  disabled,
}) {
  function handleChange(field, value) {
    onChange({ ...eventData, [field]: value });
  }

  // Build trip options for select
  const tripOptions = [
    { value: '', label: 'No trip' },
    ...trips.map(trip => ({
      value: trip.id,
      label: trip.name,
    })),
  ];

  return (
    <>
      {/* Title */}
      <FormField label="Title" required error={errors.title}>
        <ValidatedInput
          type="text"
          value={eventData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          error={errors.title}
          placeholder="Flight to Paris"
          maxLength={LIMITS.TITLE_MAX}
          disabled={disabled}
        />
      </FormField>

      {/* Category */}
      <FormField label="Category" required error={errors.category}>
        <div className="grid grid-cols-4 gap-2">
          {EVENT_CATEGORIES.map(cat => {
            const isSelected = eventData.category === cat.value;
            const category = getEventCategory(cat.value);
            const categoryStyle = getCategoryStyle(category.colorVar);
            
            return (
              <button
                key={cat.value}
                type="button"
                onClick={() => handleChange('category', cat.value)}
                disabled={disabled}
                className="p-2 rounded-sm text-center transition-all disabled:opacity-50"
                style={isSelected ? {
                  backgroundColor: categoryStyle.backgroundColor,
                  border: `2px solid ${categoryStyle.color}`,
                  color: categoryStyle.color,
                } : {
                  backgroundColor: 'var(--color-bg-raised)',
                  border: `2px solid ${errors.category ? 'var(--color-status-error)' : 'var(--color-border-subtle)'}`,
                  color: 'var(--color-text-secondary)',
                }}
              >
                <div className="flex justify-center">
                  {getCategoryIcon(cat.value, { size: 24 })}
                </div>
                <div className="text-xs font-medium mt-1 truncate">{cat.label}</div>
              </button>
            );
          })}
        </div>
      </FormField>

      {/* Trip (optional) */}
      {trips.length > 0 && (
        <FormField label="Trip">
          <Select
            options={tripOptions}
            value={eventData.trip_id || ''}
            onChange={(value) => handleChange('trip_id', value || null)}
            disabled={disabled}
            placeholder="Assign to a trip..."
          />
        </FormField>
      )}

      {/* Departure/Start Date & Time */}
      <div className="grid grid-cols-2 gap-4">
        <FormField 
          label={needsSeparateTimezones ? 'Departure Date' : 'Start Date'} 
          required 
          error={errors.startDate}
        >
          <DatePicker
            value={eventData.startDate}
            onChange={(value) => onChange({ 
              ...eventData, 
              startDate: value,
              endDate: eventData.endDate < value ? value : eventData.endDate
            })}
            error={errors.startDate}
            disabled={disabled}
          />
        </FormField>
        <FormField 
          label={needsSeparateTimezones ? 'Departure Time' : 'Start Time'} 
          required 
          error={errors.startTime}
        >
          <TimePicker
            value={eventData.startTime}
            onChange={(value) => handleChange('startTime', value)}
            error={errors.startTime}
            disabled={disabled}
          />
        </FormField>
      </div>

      {/* Departure/Main Timezone */}
      <FormField 
        label={needsSeparateTimezones ? 'Departure Timezone' : 'Timezone'} 
        required 
        error={errors.departure_timezone}
      >
        <Select
          options={TIMEZONE_OPTIONS}
          value={eventData.departure_timezone}
          onChange={(value) => onChange({ 
            ...eventData, 
            departure_timezone: value,
            arrival_timezone: needsSeparateTimezones ? eventData.arrival_timezone : value
          })}
          error={errors.departure_timezone}
          disabled={disabled}
        />
      </FormField>

      {/* Arrival/End Date & Time */}
      <div className="grid grid-cols-2 gap-4">
        <FormField 
          label={needsSeparateTimezones ? 'Arrival Date' : 'End Date'} 
          required 
          error={errors.endDate}
        >
          <DatePicker
            value={eventData.endDate}
            onChange={(value) => handleChange('endDate', value)}
            min={eventData.startDate}
            error={errors.endDate}
            disabled={disabled}
          />
        </FormField>
        <FormField 
          label={needsSeparateTimezones ? 'Arrival Time' : 'End Time'} 
          required 
          error={errors.endTime}
        >
          <TimePicker
            value={eventData.endTime}
            onChange={(value) => handleChange('endTime', value)}
            error={errors.endTime}
            disabled={disabled}
          />
        </FormField>
      </div>

      {/* Arrival Timezone (only for flights/transit) */}
      {needsSeparateTimezones && (
        <FormField label="Arrival Timezone" required error={errors.arrival_timezone}>
          <Select
            options={TIMEZONE_OPTIONS}
            value={eventData.arrival_timezone}
            onChange={(value) => handleChange('arrival_timezone', value)}
            error={errors.arrival_timezone}
            disabled={disabled}
          />
        </FormField>
      )}

      {/* Location */}
      <FormField label="Location" error={errors.location}>
        <ValidatedInput
          type="text"
          value={eventData.location}
          onChange={(e) => handleChange('location', e.target.value)}
          error={errors.location}
          placeholder="CDG Airport, Paris"
          maxLength={LIMITS.LOCATION_MAX}
          disabled={disabled}
        />
      </FormField>

      {/* Travel Details Section (only for flights/transit) */}
      {needsSeparateTimezones && (
        <TravelDetailsSection
          eventData={eventData}
          onChange={onChange}
          errors={errors}
          isExpanded={showTravelDetails}
          onToggle={onToggleTravelDetails}
          disabled={disabled}
        />
      )}

      {/* Notes */}
      <FormField label="Notes" error={errors.notes}>
        <ValidatedTextarea
          value={eventData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          error={errors.notes}
          className="min-h-[80px]"
          placeholder="Additional notes..."
          maxLength={LIMITS.NOTES_MAX}
          disabled={disabled}
        />
      </FormField>

      {/* Attachments Section */}
      <AttachmentSection
        isEditing={isEditing}
        isExpanded={showAttachments}
        onToggle={onToggleAttachments}
        attachments={attachments}
        onAttachmentsChange={onAttachmentsChange}
        eventId={eventId}
        loadingAttachments={loadingAttachments}
        pendingAttachments={pendingAttachments}
        onPendingChange={onPendingChange}
        tempId={tempId}
        disabled={disabled}
        uploadError={uploadError}
      />
    </>
  );
}
